import React, { useReducer, useEffect, useContext } from "react";
import moduleConfig from "./moduleConfig";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import {
  createReducer,
  createInitialState,
} from "../../../reducers/module/createReducer";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import { onSubmitGeneral } from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";

const MenuCreate = () => {
  const [createState, createDispatch] = useReducer(
    createReducer,
    createInitialState
  );
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  let navigate = useNavigate();

  useEffect(() => {
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      moduleConfig.breadcrumbs.create,
    ]);
  }, []);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {createState.status === "fetchFailed" ? (
        <BlockErrorMessage error={createState.error} />
      ) : (
        <>
          {/* {console.log(createState.data)} */}
          {createState.status !== "fetching" && (
            <>
              <ModuleHeader
                moduleTitle={`Create ${moduleConfig.singleItemTitle}`}
                moduleUrl={moduleConfig.url}
              ></ModuleHeader>

              <section className="content">
                <Card>
                  <Card.Body>
                    {createState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        title: "",
                        foodTime: "",
                        hotOrCold: "",
                        vegOrNonVeg: "",
                        file: "",
                        description: "",
                        nutritionalInfo: "",
                        sortOrder: "",
                        status: "0",
                      }}
                      validationSchema={yup.object({
                        title: yup
                          .string()
                          .required("Title is required.")
                          .max(50, "Title should be at most 50 characters"),
                        foodTime: yup
                          .string()
                          .required("Food time is required."),
                        hotOrCold: yup
                          .string(),
                        vegOrNonVeg: yup
                          .string().required('Food type is required.'),
                        file: yup
                          .mixed()
                          .required("Image is required")
                          .imageTypeCheck(
                            moduleConfig.imageRequirement.allowedExtensions
                          )
                          .imageSizeCheck(moduleConfig.imageRequirement.size),
                        description: yup
                          .string()
                          .required("Description is required."),
                        nutritionalInfo: yup
                          .string(),
                        sortOrder: yup
                          .number()
                          .required("Sort order is required."),
                        status: yup.string().required("Status is required."),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        const formData = new FormData();
                        for (let name in values) {
                          if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          moduleConfig.url,
                          moduleConfig.url,
                          createDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {createState.error && (
                            <FormErrorMessage error={createState.error} />
                          )}

                          {/* {console.log(form.errors)} */}
                          <CMSFormInput
                            name="title"
                            label="Title"
                            required={true}
                            form={form}
                          />
                          <CMSFormSelect
                            name="foodTime"
                            label="Food Time"
                            required={true}
                            form={form}
                            options={[
                              { label: "Breakfast", value: "Breakfast" },
                              { label: "Lunch", value: "Lunch" },
                              {
                                label: "Afternoon Snack",
                                value: "Afternoon Snack",
                              },
                              { label: "Dinner", value: "Dinner" },
                            ]}
                          />
                          <CMSFormSelect
                            name="hotOrCold"
                            label="Hot Or Cold"
                            form={form}
                            options={[
                              { label: "Hot", value: "Hot" },
                              { label: "Cold", value: "Cold" },
                            ]}
                          />
                          <CMSFormSelect
                            name="vegOrNonVeg"
                            label="Veg Or Non veg"
                            required={true}
                            form={form}
                            options={[
                              { label: "Veg", value: "Veg" },
                              { label: "Non Veg", value: "Non Veg" },
                            ]}
                          />
                          <CMSFormRichText
                            name="description"
                            label="Description"
                            form={form}
                            required={true}
                          />

                          <CMSFormImageWithCrop
                            label="Image"
                            name="file"
                            info="Recommended dimensions 500*500"
                            aspect={1 / 1}
                            outputSize={imageRequirement.dimension}
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="nutritionalInfo"
                            label="Nutritional Info"
                            info="Add texts in a comma seperated way"
                            required={true}
                            form={form}
                          />

                          <CMSFormInput
                            name="sortOrder"
                            label="Sort Order"
                            form={form}
                            required={true}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />

                          <button
                            type="submit"
                            onClick={() => form.handleSubmit}
                            className="btn btn-primary"
                          >
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            </>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default MenuCreate;
